import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { URLParamStrategy } from "../types";
import { encodeSearchToURL, decodeSearchFromURL } from "../utils/urlEncoding";

interface UseSearchStateProps {
  urlParamStrategy: URLParamStrategy;
}

export function useSearchState({ urlParamStrategy }: UseSearchStateProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Decode current search from URL
  const { search: searchValue, field: searchField } = decodeSearchFromURL(
    searchParams,
    urlParamStrategy
  );

  // Update search in URL
  const updateSearch = useCallback((value: string, field?: string) => {
    const newParams = new URLSearchParams(searchParams);
    
    encodeSearchToURL(newParams, value, field, urlParamStrategy);
    
    // Reset pagination
    newParams.set("page", "0");
    
    setSearchParams(newParams);
  }, [searchParams, setSearchParams, urlParamStrategy]);

  // Clear search
  const clearSearch = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    
    // Remove search-related params
    newParams.delete("search");
    newParams.delete("search_field");
    newParams.set("page", "0");
    
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Check if search has value
  const hasSearchValue = !!searchValue?.trim();

  return {
    searchValue: searchValue || "",
    searchField: searchField || "all",
    updateSearch,
    clearSearch,
    hasSearchValue
  };
}

